import {
  CopyButton,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { RESULT_CARD, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  type DiagramSide,
  layoutRightTriangle,
  RightTriangleDiagram,
} from "@/components/tools/linear-algebra/right-triangle-diagram";
import type { PYTHAGOREAN_THEOREM_CALCULATOR_STRINGS } from "@/components/tools/pythagorean-theorem-calculator";
import { t } from "@/lib/i18n/interpolate";
import {
  formatQuad,
  type QuadNumber,
} from "@/lib/tool-kit/linear-algebra/quad-number";
import {
  type Classification,
  type RightTriangle,
  radicalNote,
  type TripleInfo,
  type Unknown,
} from "@/lib/tool-kit/linear-algebra/right-triangle";
import {
  formatStyled,
  mulR,
  type NumberStyle,
  type Rational,
} from "@/lib/tool-kit/math/rational";

type Strings = typeof PYTHAGOREAN_THEOREM_CALCULATOR_STRINGS;

export interface PythCtx {
  strings: Strings;
  style: NumberStyle;
  mark: string;
  fmt: (value: number, options?: Intl.NumberFormatOptions) => string;
}

const NEEDS_BRACKETS = /^−|\//;

function val(ctx: PythCtx, x: Rational): string {
  return formatStyled(x, ctx.style, ctx.mark);
}

function br(ctx: PythCtx, x: Rational): string {
  const text = val(ctx, x);
  return NEEDS_BRACKETS.test(text) ? `(${text})` : text;
}

function quad(ctx: PythCtx, x: QuadNumber): string {
  return formatQuad(x, ctx.style, ctx.mark);
}

function withDecimal(ctx: PythCtx, x: QuadNumber, decimal: number): string {
  const exact = quad(ctx, x);
  return x.b.num === BigInt(0)
    ? exact
    : `${exact} ≈ ${ctx.fmt(decimal, { maximumFractionDigits: 6 })}`;
}

function tripleText(info: TripleInfo): string {
  return `(${info.primitive.join(", ")})`;
}

function TripleNote({ info, ctx }: { info: TripleInfo; ctx: PythCtx }) {
  const { strings } = ctx;
  const unit = info.factor.num === info.factor.den;
  let key: "triplePrimitive" | "tripleInteger" | "tripleScaled" =
    "tripleScaled";
  if (info.integers) {
    key = unit ? "triplePrimitive" : "tripleInteger";
  }
  return (
    <p className={`${RESULT_CARD} text-sm`}>
      {t(strings[key], {
        triple: tripleText(info),
        factor: val(ctx, info.factor),
      })}
    </p>
  );
}

function radicalLine(ctx: PythCtx, radicand: Rational): string {
  const { strings } = ctx;
  if (radicand.den !== BigInt(1)) {
    return "";
  }
  const note = radicalNote(radicand.num);
  const n = radicand.num.toString();
  if (note.kind === "perfect") {
    return "";
  }
  if (note.kind === "simplified") {
    return t(strings.noteSimplified, {
      n,
      square: (note.coefficient * note.coefficient).toString(),
      rad: note.radicand.toString(),
      coef: note.coefficient.toString(),
    });
  }
  return t(strings.noteSimplest, { n });
}

function Working({ result, ctx }: { result: RightTriangle; ctx: PythCtx }) {
  const { strings } = ctx;
  const [first, second] = result.known;
  const squares = (x: Rational) => val(ctx, mulR(x, x));
  const exact = quad(ctx, solvedSide(result));
  const decimal = ctx.fmt(solvedDecimal(result), { maximumFractionDigits: 6 });
  const radicandText = val(ctx, result.radicand);
  const wrapped =
    result.radicand.den === BigInt(1) ? radicandText : `(${radicandText})`;
  const alreadySimple = exact === `√${radicandText}`;
  let rootLine = t(strings.workingRoot, {
    side: result.unknown,
    radicand: wrapped,
    root: withDecimal(ctx, solvedSide(result), solvedDecimal(result)),
  });
  if (alreadySimple) {
    rootLine = t(strings.workingRootSimple, {
      side: result.unknown,
      radicand: wrapped,
      decimal,
    });
  }
  const extra = radicalLine(ctx, result.radicand);
  const equation =
    result.unknown === "c"
      ? t(strings.workingHypotenuse, {
          a: br(ctx, first.value),
          b: br(ctx, second.value),
          a2: squares(first.value),
          b2: squares(second.value),
          sum: val(ctx, result.radicand),
        })
      : t(strings.workingLeg, {
          side: result.unknown,
          other: first.name === "c" ? second.name : first.name,
          c: br(ctx, (first.name === "c" ? first : second).value),
          o: br(ctx, (first.name === "c" ? second : first).value),
          c2: squares((first.name === "c" ? first : second).value),
          o2: squares((first.name === "c" ? second : first).value),
          diff: val(ctx, result.radicand),
        });
  return (
    <div className="space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed">
      <p>{strings.theorem}</p>
      <p>{equation}</p>
      <p>{rootLine}</p>
      {extra === "" ? null : <p>{extra}</p>}
    </div>
  );
}

function solvedSide(result: RightTriangle): QuadNumber {
  return result[result.unknown];
}

function solvedDecimal(result: RightTriangle): number {
  return result.decimals[result.unknown];
}

export function SolveResults({
  result,
  ctx,
}: {
  result: RightTriangle;
  ctx: PythCtx;
}) {
  const { strings } = ctx;
  const sideLabel = (name: Unknown): DiagramSide => ({
    label: `${name} = ${quad(ctx, result[name])}`,
    highlight: name === result.unknown,
  });
  const layout = layoutRightTriangle(result.decimals.a, result.decimals.b);
  const answer = withDecimal(ctx, solvedSide(result), solvedDecimal(result));
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={t(strings.solvedLabel, { side: result.unknown })}
          value={answer}
        />
        <StatCard
          label={strings.areaLabel}
          value={withDecimal(
            ctx,
            result.area,
            (result.decimals.a * result.decimals.b) / 2
          )}
        />
        <StatCard
          label={strings.perimeterLabel}
          value={withDecimal(
            ctx,
            result.perimeter,
            result.decimals.a + result.decimals.b + result.decimals.c
          )}
        />
      </StatGrid>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResult}
        small
        text={`${result.unknown} = ${answer}`}
      />
      <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
      <Working ctx={ctx} result={result} />
      {result.triple ? (
        <TripleNote ctx={ctx} info={result.triple} />
      ) : (
        <p className="text-muted-foreground text-sm">{strings.notTriple}</p>
      )}
      <h3 className={WIDGET_H3}>{strings.diagramHeading}</h3>
      <RightTriangleDiagram
        a={result.decimals.a}
        b={result.decimals.b}
        downloadLabel={strings.downloadPng}
        sideA={sideLabel("a")}
        sideB={sideLabel("b")}
        sideC={sideLabel("c")}
        title={strings.diagramTitle}
      />
      {layout.notToScale ? (
        <p className="text-muted-foreground text-xs">{strings.notToScale}</p>
      ) : null}
    </>
  );
}

export function CheckResults({
  result,
  ctx,
}: {
  result: Classification;
  ctx: PythCtx;
}) {
  const { strings } = ctx;
  const [a, b, c] = result.sorted;
  if (!result.valid || result.kind === null) {
    return (
      <Notice>
        {t(strings.noTriangle, {
          a: val(ctx, a),
          b: val(ctx, b),
          c: val(ctx, c),
        })}
      </Notice>
    );
  }
  const relation = { right: "=", acute: ">", obtuse: "<" }[result.kind];
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.verdictLabel}
          value={strings.kinds[result.kind]}
        />
        <StatCard
          label={strings.sumSquaresLabel}
          value={val(ctx, result.sumOfSquares)}
        />
        <StatCard
          label={strings.longestSquaredLabel}
          value={val(ctx, result.longestSquared)}
        />
      </StatGrid>
      <div className="space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm">
        <p>
          {t(strings.checkWorking, {
            a: br(ctx, a),
            b: br(ctx, b),
            c: br(ctx, c),
            sum: val(ctx, result.sumOfSquares),
            longest: val(ctx, result.longestSquared),
            relation,
          })}
        </p>
        <p>{strings.kindExplain[result.kind]}</p>
      </div>
      {result.triple ? <TripleNote ctx={ctx} info={result.triple} /> : null}
      {result.kind === "right" ? (
        <>
          <h3 className={WIDGET_H3}>{strings.diagramHeading}</h3>
          <RightTriangleDiagram
            a={Number(a.num) / Number(a.den)}
            b={Number(b.num) / Number(b.den)}
            downloadLabel={strings.downloadPng}
            sideA={{ label: `a = ${val(ctx, a)}` }}
            sideB={{ label: `b = ${val(ctx, b)}` }}
            sideC={{ label: `c = ${val(ctx, c)}` }}
            title={strings.diagramTitle}
          />
        </>
      ) : null}
    </>
  );
}
